import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getTeamsSubPageId } from "../../TeamsHost";
import { resolveOiDeepLinkRoute } from "./deepLink";

/**
 * Applies Teams deep links (page.subPageId) to the O&I tab.
 *
 * - On mount: a valid deep link wins; otherwise `restoreLastRoute` runs when the path is exactly /Oi.
 * - While mounted: the Teams context is re-read when the tab regains focus/visibility, so a link clicked
 *   while the O&I tab is already open still opens the right record. Each subPageId is applied once,
 *   so a stale value never pulls the user back after they navigate manually.
 */
export function useOiDeepLink(restoreLastRoute: () => void) {
  const navigate = useNavigate();
  const location = useLocation();
  const handledSubPageId = useRef<string | null>(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    let cancelled = false;

    const applyDeepLink = async (isInitial: boolean) => {
      try {
        const subPageId = await getTeamsSubPageId();
        if (cancelled) return;

        if (subPageId && subPageId !== handledSubPageId.current) {
          const route = resolveOiDeepLinkRoute(subPageId);
          // Mark as handled even when invalid so unknown values are not re-evaluated on every focus.
          handledSubPageId.current = subPageId;
          if (route) {
            if (route !== pathRef.current) {
              navigate(route, { replace: isInitial });
            }
            return;
          }
        }
      } catch (error) {
        console.error("Failed to read Teams deep link context", error);
      }

      if (isInitial && !cancelled && /^\/Oi\/?$/i.test(pathRef.current)) {
        restoreLastRoute();
      }
    };

    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      void applyDeepLink(false);
    };

    void applyDeepLink(true);

    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
